import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const MODULE_ID = 'module-1';

export function LandingPage() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState(false);

  const handleStartModule = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError(true);
      return;
    }
    // Store the educator name so ModulePage can pick it up
    localStorage.setItem('itari_educator_name', trimmed);
    navigate(`/module/${MODULE_ID}`);
  };

  const parts = [
    { title: 'Human Intelligence', color: '#4A6741', description: 'What makes your thinking uniquely human' },
    { title: 'Artificial Intelligence', color: '#3B5998', description: 'How artificial intelligence actually works' },
    { title: 'Collaborative Intelligence', color: '#8B6914', description: 'What happens when human and AI work together' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Accent bar */}
      <div className="h-1 bg-gradient-to-r from-[#4A6741] via-[#3B5998] to-[#8B6914]" />

      <div className="max-w-3xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-slate-900 dark:text-slate-100 mb-6 leading-tight">
            Understanding Collaborative<br />Intelligence
          </h1>
          <p className="text-xl text-slate-600 dark:text-slate-400 max-w-xl mx-auto leading-relaxed">
            Explore what intelligence means: yours, AI's, and what happens when you work together
          </p>
        </div>

        {/* Module card */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden mb-12 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          {/* Card accent top */}
          <div className="h-1 bg-gradient-to-r from-[#4A6741] via-[#3B5998] to-[#8B6914]" />

          <div className="p-8 md:p-10">
            {/* Module meta */}
            <div className="flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400 mb-8">
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                ~60 minutes
              </span>
              <span className="text-slate-300 dark:text-slate-600">|</span>
              <span>3 parts</span>
              <span className="text-slate-300 dark:text-slate-600">|</span>
              <span>Interactive</span>
            </div>

            {/* Parts preview */}
            <div className="space-y-3 mb-10">
              {parts.map((part, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-4 p-4 rounded-xl border border-slate-100 dark:border-slate-700 hover:border-slate-200 dark:hover:border-slate-600 transition-colors animate-fade-in-up"
                  style={{ animationDelay: `${0.15 + idx * 0.05}s` }}
                >
                  <div
                    className="w-2 h-10 rounded-full flex-shrink-0"
                    style={{ backgroundColor: part.color }}
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-serif font-semibold text-slate-900 dark:text-slate-100">
                      Part {idx + 1}: {part.title}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {part.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Educator name input */}
            <div className="mb-6">
              <label
                htmlFor="educator-name"
                className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
              >
                Your name
              </label>
              <input
                id="educator-name"
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (nameError) setNameError(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleStartModule();
                }}
                placeholder="e.g. Nikola Tesla"
                className={`w-full sm:w-80 px-4 py-2.5 rounded-lg border text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#4A6741]/50 transition-colors ${
                  nameError
                    ? 'border-red-400 dark:border-red-500'
                    : 'border-slate-200 dark:border-slate-600'
                }`}
              />
              {nameError && (
                <p className="mt-1.5 text-sm text-red-500">
                  Please enter your name so we can save your progress.
                </p>
              )}
              <p className="mt-1.5 text-xs text-slate-400 dark:text-slate-500">
                Used to save your progress — you can resume later with the same name.
              </p>
            </div>

            {/* CTA */}
            <button
              onClick={handleStartModule}
              className="w-full sm:w-auto px-10 py-3.5 text-white font-medium rounded-xl transition-all hover:opacity-90 active:scale-[0.98] text-lg"
              style={{ backgroundColor: '#4A6741' }}
            >
              Begin Module
            </button>
          </div>
        </div>

        {/* Subtle footer */}
        <p className="text-center text-xs text-slate-400 dark:text-slate-600">
          Powered by Indus Training and Research Institute
        </p>
      </div>
    </div>
  );
}
